import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaShoppingCart } from "react-icons/fa";
import Layout from "../../components/Layout";
import Button from "../../components/ui/Button";
import { getProductById } from "../../utils/mockData";
import { Product } from "../../interfaces/product.interface";
import { useCart } from "../../context/CartContext";

const ProductDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(["products", "common"]);
  const { addItem } = useCart();
  const [product, setProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [showAdded, setShowAdded] = useState(false);

  React.useEffect(() => {
    const foundProduct = getProductById(id || "");
    if (foundProduct) {
      setProduct(foundProduct);
    }
  }, [id]);

  const handleAddToCart = () => {
    if (!product) return;
    addItem({ product, quantity });
    setShowAdded(true);
    window.setTimeout(() => setShowAdded(false), 3000);
  };

  if (!product) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-neon-green"></div>
        </div>
      </Layout>
    );
  }

  const discountPercentage = product.originalPrice
    ? Math.round(
        ((product.originalPrice - product.price) / product.originalPrice) * 100
      )
    : 0;

  return (
    <Layout showBackButton>
      <div className="bg-space-light/50 border border-white/10 rounded-2xl p-6 md:p-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12 items-start">
          <div className="w-full overflow-hidden rounded-xl bg-space border border-white/10">
            <img
              src={product.imageUrl}
              alt={product.name}
              className="w-full aspect-[16/10] object-cover object-center"
            />
          </div>

          <div>
            <nav className="text-body-sm text-gray-500 mb-4">
              <Link to="/" className="hover:text-neon-green transition-colors">
                {t("common:home") || "首頁"}
              </Link>
              <span className="mx-2">/</span>
              <Link
                to="/products"
                className="hover:text-neon-green transition-colors"
              >
                {t("all_products") || "港式 AI 服務"}
              </Link>
              <span className="mx-2">/</span>
              <span className="text-gray-400">{product.category}</span>
            </nav>

            <h1
              className="text-h2 md:text-h1 text-white mb-6"
              style={{ lineHeight: "125%" }}
            >
              {product.name}
            </h1>

            <div className="flex items-baseline gap-4 mb-6 flex-wrap">
              <span className="text-h1 text-neon-green tabular-nums">
                HKD {product.price.toLocaleString()}
              </span>
              {product.originalPrice && (
                <>
                  <span className="text-body-lg text-gray-500 line-through tabular-nums">
                    HKD {product.originalPrice.toLocaleString()}
                  </span>
                  <span className="bg-neon-green/20 text-neon-green px-2 py-1 rounded text-body-sm font-bold">
                    -{discountPercentage}%
                  </span>
                </>
              )}
            </div>

            {product.savingsLabel && (
              <p className="text-body-sm text-neon-green font-semibold mb-6">
                {product.savingsLabel}
              </p>
            )}

            {product.highlights && (
              <ul className="flex flex-wrap gap-2 mb-6">
                {product.highlights.map((item) => (
                  <li
                    key={item}
                    className="text-caption px-2 py-1 rounded-md bg-white/5 text-gray-300 border border-white/10"
                  >
                    {item}
                  </li>
                ))}
              </ul>
            )}

            <p className="text-body text-gray-300 mb-8 leading-relaxed">
              {product.description}
            </p>

            {showAdded && (
              <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-neon-green/40 bg-neon-green/10 px-4 py-3">
                <p className="text-body-sm text-neon-green font-semibold">
                  ✓ {t("added_to_cart")}
                </p>
                <button
                  type="button"
                  onClick={() => navigate("/cart")}
                  className="text-body-sm text-white underline hover:text-neon-green transition-colors text-left"
                >
                  {t("view_cart")} →
                </button>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center border border-white/20 rounded-lg bg-space/50 shrink-0">
                <button
                  type="button"
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-4 py-2 text-body text-gray-300 hover:text-neon-green transition-colors"
                >
                  -
                </button>
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) =>
                    setQuantity(Math.max(1, parseInt(e.target.value, 10) || 1))
                  }
                  className="w-12 py-2 text-center text-body border-none focus:ring-0 bg-transparent text-white tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                <button
                  type="button"
                  onClick={() => setQuantity(quantity + 1)}
                  className="px-4 py-2 text-body text-gray-300 hover:text-neon-green transition-colors"
                >
                  +
                </button>
              </div>
              <Button
                size="lg"
                type="button"
                onClick={handleAddToCart}
                className="flex-1 flex items-center justify-center gap-2 bg-neon-green text-space hover:opacity-90"
              >
                <FaShoppingCart />
                {t("add_to_cart") || "加入購物車"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProductDetail;
